import { useState, useMemo, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { AppLayout } from "@/components/layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useSearchTrainersAssistant, useListTrainers, useGetGymAiAssistantPublic } from "@workspace/api-client-react";
import { format } from "date-fns";
import { Star, Clock, ChevronRight, ChevronLeft, ArrowRight, Sparkles, User, Calendar, Users } from "lucide-react";
import { Link } from "wouter";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const AVATAR_PRESETS: Record<string, string> = {
  "preset:coach-male": "🏋️",
  "preset:coach-female": "🤸",
  "preset:sensei": "🥋",
  "preset:bot": "🤖",
  "preset:zen": "🧘",
};

function getMessages(preset: string, name: string, step: string) {
  const msgs: Record<string, Record<string, string>> = {
    friendly: {
      intro: `Hey there! I'm ${name}, and I'm here to help you find the perfect trainer. Let's get started! 😊`,
      specialty: "What kind of training are you looking for? Pick what sounds fun!",
      schedule: "When works best for you? No pressure — pick what fits your vibe!",
      gender: "Almost done! Do you have a preference for your trainer?",
      results: "Here are your matches! I think you'll love training with any of them! 🎉",
      noResults: "Hmm, no exact matches — but don't worry! Try adjusting your preferences. 💛",
    },
    professional: {
      intro: `Welcome. I'm ${name}, your scheduling assistant. I'll help you find a suitable trainer based on your preferences.`,
      specialty: "Please select your preferred discipline from the options below.",
      schedule: "Indicate your preferred training days and time window.",
      gender: "Do you have a trainer gender preference? This is optional.",
      results: "Based on your criteria, here are the recommended matches.",
      noResults: "No exact matches were found. Consider broadening your search criteria.",
    },
    motivational: {
      intro: `LET'S GO! I'm ${name}, and I'm pumped to help you find an amazing trainer! 💪`,
      specialty: "What discipline gets you FIRED UP? Let's find your perfect match!",
      schedule: "When do you want to CRUSH IT? Pick your power hours!",
      gender: "One more thing before we find your champion coach!",
      results: "BOOM! Here are your top matches — these trainers are READY for you! 🔥",
      noResults: "No worries, champion! Let's adjust and try again — your perfect trainer is out there!",
    },
    zen: {
      intro: `Welcome, friend. I'm ${name}. Take a breath, and let's find the right training partner for your journey. 🙏`,
      specialty: "What practice calls to you? Let your intuition guide you.",
      schedule: "When does your body feel most ready to train? Honor your natural rhythm.",
      gender: "Is there a preference that would help you feel most comfortable?",
      results: "The universe has aligned these trainers with your path. Namaste. ✨",
      noResults: "Sometimes the path takes an unexpected turn. Let's explore other options mindfully.",
    },
  };
  return msgs[preset]?.[step] || msgs.friendly[step] || "";
}

type WizardStep = "specialty" | "schedule" | "gender" | "results";

function AssistantAvatar({ avatarUrl, name, size = "sm" }: { avatarUrl: string | null; name: string; size?: "sm" | "lg" }) {
  const sizeClass = size === "lg" ? "w-12 h-12" : "w-8 h-8";
  const textSize = size === "lg" ? "text-xl" : "text-sm";
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [avatarUrl]);

  if (avatarUrl && !avatarUrl.startsWith("preset:") && !imgError) {
    return (
      <div className={`${sizeClass} rounded-full bg-secondary flex items-center justify-center shrink-0 overflow-hidden ring-2 ring-primary/20`}>
        <img src={avatarUrl} alt={name} className="w-full h-full object-cover" onError={() => setImgError(true)} />
      </div>
    );
  }

  const emoji = avatarUrl && !imgError ? AVATAR_PRESETS[avatarUrl] : null;
  return (
    <div className={`${sizeClass} rounded-full bg-secondary flex items-center justify-center ${textSize} shrink-0 ring-2 ring-primary/20`}>
      {emoji || name.charAt(0).toUpperCase()}
    </div>
  );
}

function AssistantBubble({ message, avatarUrl, name }: { message: string; avatarUrl: string | null; name: string }) {
  return (
    <div className="flex gap-3 items-start">
      <AssistantAvatar avatarUrl={avatarUrl} name={name} />
      <div className="bg-white/5 rounded-2xl rounded-tl-sm px-4 py-3 text-sm text-white/90 max-w-[80%] border border-white/5">
        {message}
      </div>
    </div>
  );
}

export default function FindTrainer() {
  const { user } = useAuth();
  const gymId = user?.gymId || "";

  const { data: assistantConfig } = useGetGymAiAssistantPublic(gymId, {
    query: { enabled: !!gymId },
  });

  const assistantName = assistantConfig?.name || "Assistant";
  const assistantAvatar = assistantConfig?.avatarUrl || null;
  const personality = assistantConfig?.personalityPreset || "friendly";

  const [step, setStep] = useState<WizardStep>("specialty");
  const [selectedSpecialty, setSelectedSpecialty] = useState<string | null>(null);
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [timePreference, setTimePreference] = useState<string>("FLEXIBLE");
  const [genderPreference, setGenderPreference] = useState<string>("NO_PREFERENCE");

  const { data: trainers } = useListTrainers(gymId, undefined, {
    query: { enabled: !!gymId },
  });

  const availableSpecialties = useMemo(() => {
    if (!trainers) return [];
    const set = new Set<string>();
    trainers.forEach(t => (t.specialties as string[] || []).forEach(s => set.add(s)));
    return Array.from(set).sort();
  }, [trainers]);

  const searchMutation = useSearchTrainersAssistant();

  const handleSearch = () => {
    searchMutation.mutate({
      data: {
        specialty: selectedSpecialty || undefined,
        dayPreferences: selectedDays.length > 0 ? selectedDays : undefined,
        timePreference: timePreference || undefined,
        genderPreference: genderPreference || undefined,
      },
    });
    setStep("results");
  };

  const toggleDay = (day: number) => {
    setSelectedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const stepIndex = { specialty: 0, schedule: 1, gender: 2, results: 3 };
  const currentIndex = stepIndex[step];

  const goBack = () => {
    if (step === "schedule") setStep("specialty");
    else if (step === "gender") setStep("schedule");
    else if (step === "results") setStep("gender");
  };

  const resetWizard = () => {
    setStep("specialty");
    setSelectedSpecialty(null);
    setSelectedDays([]);
    setTimePreference("FLEXIBLE");
    setGenderPreference("NO_PREFERENCE");
  };

  return (
    <AppLayout>
      <div className="flex flex-col gap-6 pb-12 max-w-3xl mx-auto">
        <div className="flex items-center gap-4 mb-2">
          <AssistantAvatar avatarUrl={assistantAvatar} name={assistantName} size="lg" />
          <div>
            <h1 className="text-3xl font-display font-bold text-white">Find a Trainer</h1>
            <p className="text-muted-foreground text-sm">with {assistantName}</p>
          </div>
        </div>

        <AssistantBubble
          message={getMessages(personality, assistantName, "intro")}
          avatarUrl={assistantAvatar}
          name={assistantName}
        />

        {step !== "results" && (
          <div className="flex items-center gap-2">
            {["Specialty", "Schedule", "Preference"].map((label, i) => (
              <div key={label} className="flex items-center gap-2">
                {i > 0 && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                  currentIndex === i
                    ? "bg-primary text-black"
                    : currentIndex > i
                      ? "bg-primary/20 text-primary"
                      : "bg-white/5 text-muted-foreground"
                }`}>
                  <span className="w-6 h-6 rounded-full bg-black/20 flex items-center justify-center text-xs">
                    {i + 1}
                  </span>
                  {label}
                </div>
              </div>
            ))}
          </div>
        )}

        {step === "specialty" && (
          <div className="space-y-4">
            <AssistantBubble
              message={getMessages(personality, assistantName, "specialty")}
              avatarUrl={assistantAvatar}
              name={assistantName}
            />

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 ml-11">
              {availableSpecialties.map(s => (
                <button
                  key={s}
                  onClick={() => setSelectedSpecialty(selectedSpecialty === s ? null : s)}
                  className={`px-4 py-4 rounded-xl text-sm font-medium transition-all border text-center ${
                    selectedSpecialty === s
                      ? "bg-primary text-black font-bold border-primary"
                      : "bg-white/5 text-white hover:bg-white/10 border-white/10"
                  }`}
                >
                  {s}
                </button>
              ))}
              {availableSpecialties.length === 0 && (
                <p className="col-span-3 text-muted-foreground text-center py-8">
                  No specialties found for this gym's trainers.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="ghost"
                className="text-muted-foreground"
                onClick={() => {
                  setSelectedSpecialty(null);
                  setStep("schedule");
                }}
              >
                Skip
              </Button>
              <Button
                onClick={() => setStep("schedule")}
                className="bg-primary text-black font-bold h-12 px-8 rounded-xl"
              >
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {step === "schedule" && (
          <div className="space-y-4">
            <AssistantBubble
              message={getMessages(personality, assistantName, "schedule")}
              avatarUrl={assistantAvatar}
              name={assistantName}
            />

            <div className="ml-11 space-y-4">
              <div>
                <label className="text-sm text-muted-foreground block mb-3">Preferred Days</label>
                <div className="flex flex-wrap gap-2">
                  {DAY_NAMES.map((name, i) => (
                    <button
                      key={i}
                      onClick={() => toggleDay(i)}
                      className={`px-4 py-3 rounded-xl text-sm font-medium transition-all border ${
                        selectedDays.includes(i)
                          ? "bg-primary text-black font-bold border-primary"
                          : "bg-white/5 text-white hover:bg-white/10 border-white/10"
                      }`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm text-muted-foreground block mb-3">Time Window</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { value: "FLEXIBLE", label: "I'm Flexible" },
                    { value: "MORNING", label: "Morning (6am-12pm)" },
                    { value: "AFTERNOON", label: "Afternoon (12pm-5pm)" },
                    { value: "EVENING", label: "Evening (5pm+)" },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setTimePreference(opt.value)}
                      className={`px-4 py-3 rounded-xl text-sm font-medium transition-all border text-center ${
                        timePreference === opt.value
                          ? "bg-primary text-black font-bold border-primary"
                          : "bg-white/5 text-white hover:bg-white/10 border-white/10"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="ghost" className="text-muted-foreground" onClick={goBack}>
                <ChevronLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button
                onClick={() => setStep("gender")}
                className="bg-primary text-black font-bold h-12 px-8 rounded-xl"
              >
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {step === "gender" && (
          <div className="space-y-4">
            <AssistantBubble
              message={getMessages(personality, assistantName, "gender")}
              avatarUrl={assistantAvatar}
              name={assistantName}
            />

            <div className="ml-11 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { value: "NO_PREFERENCE", label: "No Preference", icon: Users },
                  { value: "MALE", label: "Male", icon: User },
                  { value: "FEMALE", label: "Female", icon: User },
                  { value: "OTHER", label: "Other", icon: User },
                ].map(opt => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => setGenderPreference(opt.value)}
                      className={`flex flex-col items-center gap-3 px-4 py-6 rounded-xl text-sm font-medium transition-all border ${
                        genderPreference === opt.value
                          ? "bg-primary text-black font-bold border-primary"
                          : "bg-white/5 text-white hover:bg-white/10 border-white/10"
                      }`}
                    >
                      <Icon className="w-6 h-6" />
                      {opt.label}
                    </button>
                  );
                })}
              </div>

              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <h3 className="text-sm font-bold text-white mb-2">Your selections:</h3>
                <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                  {selectedSpecialty && (
                    <span className="px-3 py-1 rounded-lg bg-primary/10 text-primary border border-primary/20">
                      {selectedSpecialty}
                    </span>
                  )}
                  {selectedDays.length > 0 ? (
                    <span className="px-3 py-1 rounded-lg bg-white/5 border border-white/10">
                      {selectedDays.map(d => DAY_SHORT[d]).join(", ")}
                    </span>
                  ) : (
                    <span className="px-3 py-1 rounded-lg bg-white/5 border border-white/10">Any Day</span>
                  )}
                  <span className="px-3 py-1 rounded-lg bg-white/5 border border-white/10">
                    {timePreference === "FLEXIBLE" ? "Any Time" : timePreference.charAt(0) + timePreference.slice(1).toLowerCase()}
                  </span>
                  {genderPreference !== "NO_PREFERENCE" && (
                    <span className="px-3 py-1 rounded-lg bg-white/5 border border-white/10">
                      {genderPreference.charAt(0) + genderPreference.slice(1).toLowerCase()} trainer
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="ghost" className="text-muted-foreground" onClick={goBack}>
                <ChevronLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button
                onClick={handleSearch}
                className="bg-primary text-black font-bold h-12 px-8 rounded-xl"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Find Trainers
              </Button>
            </div>
          </div>
        )}

        {step === "results" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {searchMutation.isPending ? "Searching..." : "Your Matches"}
              </h2>
              <Button variant="ghost" className="text-muted-foreground" onClick={resetWizard}>
                Start Over
              </Button>
            </div>

            {searchMutation.isPending && (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-48 bg-white/5 rounded-2xl animate-pulse" />
                ))}
              </div>
            )}

            {searchMutation.data && (
              <>
                {searchMutation.data.matches.length > 0 && (
                  <AssistantBubble
                    message={getMessages(personality, assistantName, "results")}
                    avatarUrl={assistantAvatar}
                    name={assistantName}
                  />
                )}

                {searchMutation.data.suggestions.length > 0 && (
                  <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                    {searchMutation.data.suggestions.map((s, i) => (
                      <p key={i} className="text-sm text-amber-300">{s}</p>
                    ))}
                  </div>
                )}

                {searchMutation.data.matches.length > 0 && (
                  <div className="space-y-4">
                    {searchMutation.data.matches.map(t => (
                      <TrainerResultCard key={t.id} trainer={t} gymId={gymId} />
                    ))}
                  </div>
                )}

                {searchMutation.data.matches.length === 0 && searchMutation.data.otherTrainers.length > 0 && (
                  <div className="space-y-4">
                    <AssistantBubble
                      message={getMessages(personality, assistantName, "noResults")}
                      avatarUrl={assistantAvatar}
                      name={assistantName}
                    />
                    <p className="text-muted-foreground text-sm ml-11">
                      No exact matches, but here are other trainers you might like:
                    </p>
                    {searchMutation.data.otherTrainers.map(t => (
                      <TrainerResultCard key={t.id} trainer={t} gymId={gymId} noSlots />
                    ))}
                  </div>
                )}

                {searchMutation.data.matches.length === 0 && searchMutation.data.otherTrainers.length === 0 && (
                  <div className="text-center py-12">
                    <AssistantBubble
                      message={getMessages(personality, assistantName, "noResults")}
                      avatarUrl={assistantAvatar}
                      name={assistantName}
                    />
                    <div className="mt-4">
                      <Link href="/book">
                        <Button variant="outline" className="border-white/10">Go to Booking Page</Button>
                      </Link>
                    </div>
                  </div>
                )}

                {searchMutation.data.matches.length > 0 && searchMutation.data.otherTrainers.length > 0 && (
                  <div className="space-y-4 pt-4">
                    <h3 className="text-lg font-bold text-white border-t border-white/10 pt-4">
                      Other Trainers
                    </h3>
                    {searchMutation.data.otherTrainers.map(t => (
                      <TrainerResultCard key={t.id} trainer={t} gymId={gymId} noSlots />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

interface TrainerResultProps {
  trainer: {
    id: string;
    firstName: string;
    lastName: string;
    avatarUrl?: string | null;
    bio?: string | null;
    specialties?: string[];
    hourlyRate?: number | null;
    avgRating?: number | null;
    totalSessions: number;
    gender?: string | null;
    availableSlots?: { start: string; end: string; trainerId?: string; locationId?: string | null }[];
    totalAvailableSlots: number;
  };
  gymId: string;
  noSlots?: boolean;
}

function TrainerResultCard({ trainer: t, gymId, noSlots }: TrainerResultProps) {
  const [showSlots, setShowSlots] = useState(false);

  return (
    <Card className="glass-panel border border-white/5 hover:border-primary/30 transition-all">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center text-xl font-bold text-white shrink-0 overflow-hidden">
            {t.avatarUrl ? (
              <img src={t.avatarUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              t.firstName[0]
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-lg font-bold text-white">{t.firstName} {t.lastName}</h3>
                <div className="flex items-center gap-2 mt-0.5">
                  {t.avgRating && (
                    <span className="flex items-center gap-1 text-sm text-primary">
                      <Star className="w-3.5 h-3.5 fill-primary" />
                      {Number(t.avgRating).toFixed(1)}
                    </span>
                  )}
                  <span className="text-sm text-muted-foreground">{t.totalSessions} sessions</span>
                </div>
              </div>
              {t.hourlyRate && (
                <span className="text-lg font-mono text-primary font-bold">${t.hourlyRate}/hr</span>
              )}
            </div>

            {t.specialties && t.specialties.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {(t.specialties as string[]).map(s => (
                  <span key={s} className="px-2.5 py-0.5 bg-white/5 rounded-lg text-xs text-muted-foreground border border-white/10">
                    {s}
                  </span>
                ))}
              </div>
            )}

            {t.bio && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{t.bio}</p>}

            {!noSlots && t.totalAvailableSlots > 0 && (
              <div className="mt-3">
                <button
                  onClick={() => setShowSlots(!showSlots)}
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  <Calendar className="w-3.5 h-3.5" />
                  {t.totalAvailableSlots} available slot{t.totalAvailableSlots !== 1 ? "s" : ""}
                  <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showSlots ? "rotate-90" : ""}`} />
                </button>
                {showSlots && t.availableSlots && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {t.availableSlots.map((slot, i) => {
                      const slotParams = new URLSearchParams({ trainerId: t.id, gymId, slotStart: slot.start });
                      if (slot.locationId) slotParams.set("slotLocationId", slot.locationId);
                      return (
                        <Link key={i} href={`/book?${slotParams.toString()}`}>
                          <span className="px-3 py-1.5 rounded-lg bg-white/5 text-xs text-white hover:bg-primary/20 hover:text-primary border border-white/10 transition-all cursor-pointer inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {format(new Date(slot.start), "EEE, MMM d")} at {format(new Date(slot.start), "h:mm a")}
                          </span>
                        </Link>
                      );
                    })}
                    {t.totalAvailableSlots > (t.availableSlots?.length || 0) && (
                      <Link href={`/book?trainerId=${t.id}&gymId=${gymId}`}>
                        <span className="px-3 py-1.5 rounded-lg bg-primary/10 text-xs text-primary border border-primary/20 cursor-pointer inline-flex items-center gap-1">
                          +{t.totalAvailableSlots - (t.availableSlots?.length || 0)} more
                        </span>
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="mt-3 flex gap-2">
              <Link href={`/book?trainerId=${t.id}&gymId=${gymId}`}>
                <Button size="sm" className="bg-primary text-black font-bold rounded-xl">
                  Book Now <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
